/**
 * Detected field boundaries on the request-form map,
 * see [`docs/GIS-DETECTED-FIELDS.md`](../../../../../../docs/GIS-DETECTED-FIELDS.md).
 *
 * Attaches to `mfs-request-openlayers-map-2`: turns on its layer button, reloads the
 * boundaries for every viewport the map reports, and feeds them back as overlay contours.
 * Keeps the request forms free of layer logic — they only add the attribute.
 */
import { Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { AiFieldBoundaryDto } from '@6grain/masofa-monolith-api-client/ai-inference';
import {
  IRequestMapOverlayPolygon,
  RequestMapComponent,
} from '@components/request-map/request-map.component2';
import {
  DETECTED_FIELDS_COLOR,
  DETECTED_FIELDS_DASH_PATTERN,
  DETECTED_FIELDS_FILL_RGBA,
  DETECTED_FIELDS_STROKE_WIDTH,
  DetectedFieldsService,
  DetectedFieldsViewport,
} from '@services/detected-fields.service';

@Directive({
  selector: 'mfs-request-openlayers-map-2[appDetectedFieldsLayer]',
  standalone: true,
})
export class DetectedFieldsMapLayerDirective implements OnInit, OnDestroy {
  private readonly requestMap = inject(RequestMapComponent);
  private readonly detectedFieldsService = inject(DetectedFieldsService);

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();
  private viewport: DetectedFieldsViewport | null = null;
  private visible = false;

  ngOnInit(): void {
    this.requestMap.overlayLayerToggle = true;
    this.requestMap.overlayLayerLabel = 'SHOW_DETECTED_FIELDS';

    this.requestMap.overlayLayerVisibleChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible: boolean) => {
        this.visible = visible;
        if (visible) {
          this.reload$.next();
        } else {
          this.requestMap.overlayPolygons = [];
        }
      });

    this.requestMap.viewportChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((viewport: DetectedFieldsViewport) => {
        this.viewport = viewport;
        if (this.visible) {
          this.reload$.next();
        }
      });

    this.reload$
      .pipe(
        debounceTime(300),
        switchMap(() => {
          if (!this.visible || !this.viewport) {
            return of(null);
          }
          return this.detectedFieldsService
            .loadBoundaries(this.viewport)
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.requestMap.overlayPolygons = result ? this.toOverlayPolygons(result.boundaries) : [];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.reload$.complete();
  }

  private toOverlayPolygons(boundaries: AiFieldBoundaryDto[]): IRequestMapOverlayPolygon[] {
    return boundaries.flatMap((boundary) => {
      const tooltipHtml = this.detectedFieldsService.buildPopupHtml(boundary);
      return this.detectedFieldsService.geometryToRings(boundary.polygon).map((coordinates) => ({
        coordinates,
        strokeColor: DETECTED_FIELDS_COLOR,
        fillColor: DETECTED_FIELDS_FILL_RGBA,
        strokeWidth: DETECTED_FIELDS_STROKE_WIDTH,
        dashArray: DETECTED_FIELDS_DASH_PATTERN,
        tooltipHtml,
      }));
    });
  }
}
